import ArticleLayout, { Section, QuickAnswerBox, CheckList } from '../components/ArticleLayout';

export default function BasetaoWarehouseGuidePage() {
  return (
    <ArticleLayout
      title="Basetao Warehouse Guide: Storage, QC Photos & Package Consolidation Explained"
      description="Everything you need to know about the Basetao warehouse process. Learn how QC photos work, storage time limits, package consolidation, inspection procedures, and tips for managing your warehouse items efficiently."
      tags={['Warehouse', 'Guide']}
      readTime="9 min read"
      tocItems={[
        { id: 'process', title: 'The Basetao Warehouse Process Explained' },
        { id: 'qc-photos', title: 'Understanding QC Photos and Inspection' },
        { id: 'storage', title: 'Warehouse Storage Time and Limits' },
        { id: 'consolidation', title: 'How Package Consolidation Works' },
        { id: 'repacking', title: 'Repacking and Protective Packaging' },
        { id: 'problems', title: 'Handling Problems and Returns' },
        { id: 'tips', title: 'Expert Warehouse Management Tips' },
      ]}
      heroImage="https://readdy.ai/api/search-image?query=Modern%20warehouse%20interior%20with%20neatly%20organized%20cardboard%20boxes%20and%20packages%20on%20shelves%2C%20clean%20logistics%20facility%2C%20warm%20industrial%20lighting%2C%20minimalist%20aesthetic%2C%20editorial%20business%20photography%2C%20no%20people%2C%20professional%20storage%20and%20distribution%20center&width=1200&height=675&seq=article-warehouse-hero-2026&orientation=landscape"
    >
      <QuickAnswerBox>
        <strong>The Basetao warehouse</strong> is a centralized facility in China where all purchased items are received, inspected, photographed, and stored before international shipping. Items can be stored for up to 90 days at no extra cost. The warehouse team takes QC photos of every item, allowing you to inspect quality before approving international shipment. Package consolidation is performed here, enabling you to bundle multiple items into one shipment for significant shipping savings.
      </QuickAnswerBox>

      <Section id="process" title="1. The Basetao Warehouse Process Explained">
        <p>
          Understanding the <strong>Basetao warehouse</strong> workflow is essential for managing your orders effectively. The process follows a clear sequence from item arrival to international departure:
        </p>
        <div className="space-y-4 mt-4">
          <div className="flex gap-3">
            <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-sm font-semibold">1</span>
            <div>
              <strong className="text-foreground-800 text-sm">Item Arrival</strong>
              <p className="text-sm text-foreground-600 mt-1">The seller ships your item to the Basetao warehouse address in China. Domestic shipping within China typically takes 1-5 days.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-sm font-semibold">2</span>
            <div>
              <strong className="text-foreground-800 text-sm">Warehouse Receipt</strong>
              <p className="text-sm text-foreground-600 mt-1">Basetao staff receive the package, check the order number, and log it into your account. You receive a notification that your item has arrived.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-sm font-semibold">3</span>
            <div>
              <strong className="text-foreground-800 text-sm">QC Photo Session</strong>
              <p className="text-sm text-foreground-600 mt-1">Staff open the package, inspect the item, and take multiple photographs from different angles. These QC photos are uploaded to your account.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-sm font-semibold">4</span>
            <div>
              <strong className="text-foreground-800 text-sm">Storage Period</strong>
              <p className="text-sm text-foreground-600 mt-1">The item is stored in the warehouse. You have up to 90 days to arrange shipping. During this time, you can review QC photos and decide whether to keep or return the item.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-sm font-semibold">5</span>
            <div>
              <strong className="text-foreground-800 text-sm">Shipping Arrangement</strong>
              <p className="text-sm text-foreground-600 mt-1">When you are ready, select the items you want to ship, choose a carrier and shipping method, and pay the international shipping fee. Basetao then packs and dispatches your package.</p>
            </div>
          </div>
        </div>
      </Section>

      <Section id="qc-photos" title="2. Understanding QC Photos and Inspection">
        <p>
          <strong>QC (Quality Control) photos</strong> are the single most valuable feature of the Basetao warehouse service. These photographs provide visual proof of what you actually received, allowing you to verify quality, accuracy, and condition before committing to expensive international shipping.
        </p>
        <p>
          A typical QC photo set includes: front view of the item, back view, close-ups of logos and details, size tags or labels, packaging condition, and any defects or discrepancies. For shoes, photos usually show both shoes, the box, insole details, and sole stamps.
        </p>
        <p>
          Experienced users recommend checking these details in QC photos: color accuracy compared to listing photos, material quality and stitching, correct size and fit indicators, logo placement and font accuracy, and any visible damage or imperfections. If anything looks wrong, request additional photos or initiate a return before shipping.
        </p>
      </Section>

      <Section id="storage" title="3. Warehouse Storage Time and Limits">
        <p>
          Basetao offers <strong>free warehouse storage for up to 90 days</strong> from the date the item arrives. This generous window allows you to accumulate multiple orders before consolidating and shipping, maximizing your shipping cost savings.
        </p>
        <p>
          After the 90-day free period, extended storage may incur fees. These fees are typically small but can add up if you leave items in the warehouse for months. The best practice is to ship items well before the free storage period expires.
        </p>
        <p>
          There is no strict limit on the number of items you can store simultaneously. However, very large orders (50+ items) may require special handling or additional fees. For most users, the warehouse easily accommodates normal shopping volumes.
        </p>
      </Section>

      <Section id="consolidation" title="4. How Package Consolidation Works">
        <p>
          <strong>Package consolidation</strong> at the Basetao warehouse is where significant cost savings happen. Instead of shipping each item in its original seller packaging, Basetao removes unnecessary packaging, bundles your items together, and ships them in one box.
        </p>
        <p>
          The consolidation process works as follows: select multiple items from your warehouse inventory, choose "submit for delivery" with all selected items, Basetao removes individual seller packaging (unless you request otherwise), items are arranged efficiently in a single box with protective padding, and the consolidated package is weighed and measured for final shipping cost calculation.
        </p>
        <p>
          The savings are substantial. A consolidated 2kg package might cost $25 to ship, while shipping four individual 500g packages could cost $12-15 each ($48-60 total). That is a potential 50% reduction in shipping costs.
        </p>
      </Section>

      <Section id="repacking" title="5. Repacking and Protective Packaging">
        <p>
          When consolidating, Basetao repacks your items into a single shipping box. By default, they remove unnecessary packaging like shoe boxes, plastic bags, and promotional materials to reduce weight and volume. However, you can request special handling:
        </p>
        <CheckList items={[
          { bold: 'Keep Original Boxes:', text: 'Request that shoe boxes or branded packaging be preserved. This adds weight but protects items better.' },
          { bold: 'Extra Padding:', text: 'Ask for additional bubble wrap or foam padding for fragile items like sunglasses or electronics.' },
          { bold: 'Waterproofing:', text: 'Request waterproof bagging for items vulnerable to moisture during transit.' },
          { bold: 'Gift Packaging:', text: 'Some users request items be kept in original packaging if they are gifts.' },
        ]} />
      </Section>

      <Section id="problems" title="6. Handling Problems and Returns">
        <p>
          If an item arrives at the warehouse and the QC photos reveal a problem, you have options before the item ships internationally. This is the critical window where problems are easiest and cheapest to resolve.
        </p>
        <p>
          For wrong items, damaged goods, or significant quality issues, you can typically request a return or exchange through Basetao support. They will coordinate with the seller to arrange the return. Most sellers accept returns for wrong or defective items, though some may charge a small restocking fee.
        </p>
        <p>
          For minor issues that are not worth the hassle of returning, you can choose to accept the item and ship it anyway. This is a common decision for inexpensive items where the return shipping within China would cost more than the item value.
        </p>
      </Section>

      <Section id="tips" title="7. Expert Warehouse Management Tips">
        <p>
          Maximize your Basetao warehouse experience with these pro strategies:
        </p>
        <div className="space-y-3 mt-4">
          <div className="bg-accent-50 rounded-xl p-4 border border-accent-200/70">
            <h3 className="text-sm font-semibold text-accent-700 mb-1">Review QC Photos Immediately</h3>
            <p className="text-sm text-foreground-600">Do not let items sit in the warehouse without checking QC photos. The sooner you spot a problem, the faster it can be resolved with the seller.</p>
          </div>
          <div className="bg-accent-50 rounded-xl p-4 border border-accent-200/70">
            <h3 className="text-sm font-semibold text-accent-700 mb-1">Consolidate Strategically</h3>
            <p className="text-sm text-foreground-600">Wait until you have 3-5 items before shipping. The per-item shipping cost drops dramatically as package size increases, but extremely heavy packages may trigger customs scrutiny.</p>
          </div>
          <div className="bg-accent-50 rounded-xl p-4 border border-accent-200/70">
            <h3 className="text-sm font-semibold text-accent-700 mb-1">Set a Shipping Reminder</h3>
            <p className="text-sm text-foreground-600">Set a calendar reminder at 60 days after your first item arrives. This gives you 30 days to decide on shipping before free storage expires.</p>
          </div>
          <div className="bg-accent-50 rounded-xl p-4 border border-accent-200/70">
            <h3 className="text-sm font-semibold text-accent-700 mb-1">Request Additional Photos When Unsure</h3>
            <p className="text-sm text-foreground-600">If QC photos are unclear, do not hesitate to ask for additional angles or close-ups. It is better to spend time reviewing now than to receive a disappointing item later.</p>
          </div>
        </div>
      </Section>
    </ArticleLayout>
  );
}